import BaseModelInstance from './base.model'
import { User as ModelDefinition } from './model-definitions'
import UsertypeService from './../services/Usertype.service'
import UserService from './../services/User.service'
import StripeCustomerService from './../services/StripeCustomer.service'
import StripePaymentService from './../services/StripePayment.service'

// Helper function to get factories when needed
const getFactories = () => {
  // This will be evaluated only when called, breaking the circular dependency
  return require('./../factories')
}

class StripeUserService {
  // User instance that this service is working on behalf of.
  _User = null

  // key: value pair of options.
  _opts = { sub_class: undefined }

  _customer = null

  async customer(customerId) {
    if (this._customer && customerId && this._customer.id === customerId) {
      return this._customer
    }

    if (customerId) {
      try {
        return (this._customer = await StripeCustomerService.get(customerId, {
          expand: ['customer', 'paymentMethod'],
        }))
      } catch (ex) {
        return Promise.reject(ex)
      }
    }

    if (this._customer) {
      return this._customer
    }

    return Promise.reject()
  }

  async customers() {
    try {
      const cust = await StripeCustomerService.search({
        search: { user_id: this._User.id() },
      })
      return Array.isArray(cust?.models) ? cust.models : []
    } catch (ex) {
      return Promise.reject(ex)
    }
  }

  async charge(payload) {
    try {
      return await StripePaymentService.store(payload)
    } catch (ex) {
      return Promise.reject(ex)
    }
  }

  async createPaymentIntents(payload) {
    try {
      return await StripePaymentService.createPaymentIntents(payload)
    } catch (ex) {
      return Promise.reject(ex)
    }
  }

  async confirmPayment(payload) {
    try {
      return await StripePaymentService.confirmPayment(payload)
    } catch (ex) {
      return Promise.reject(ex)
    }
  }

  constructor(User, opts) {
    this._User = User
    this._opts = opts && typeof opts === 'object' ? opts : {}
  }
}

class User extends BaseModelInstance {
  afterSet = (key, value) => {}

  name = () =>
    [this.get('u_fname'), this.get('u_lname')]
      .filter(n => n)
      .join(' ')
      .trim()

  isA = usertype => {
    usertype = Array.isArray(usertype) ? usertype : [usertype]
    for (const u in usertype) {
      if (UsertypeService.isA(usertype[u], this.get('usertype_id'))) {
        return true
      }
    }
    return false
  }

  hasCompletedTrainings = async () => {
    try {
      return await UserService.hasCompletedTrainings(this.id())
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  getRequiredTrainings = async () => {
    try {
      return await UserService.hasCompletedTrainings(this.id(), {
        expand: true,
      })
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  hasCompletedTerms = async () => {
    try {
      return await UserService.hasCompletedTerms(this.id())
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  getRequiredTerms = async () => {
    try {
      return await UserService.hasCompletedTerms(this.id(), { expand: true })
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  hasCompletedGoals = async () => {
    try {
      return await UserService.hasCompletedGoals(this.id())
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  getRequiredGoals = async () => {
    try {
      return await UserService.hasCompletedGoals(this.id(), { expand: true })
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  hasMetRequirements = async requirements => {
    try {
      return await UserService.hasMetRequirements(this.id(), requirements)
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  signTerms = async request => {
    request = request && typeof request === 'object' ? request : {}
    // request.term_slugs 	= Array<string>
    // request.term_ids 		= Array<number>
    // request.accepted_ip 	= string
    //

    try {
      return await UserService.signTerms(this.id(), request)
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  getDownline = async () => {
    try {
      return await UserService.getDownline(this.id())
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  getDownlineLeaders = async () => {
    try {
      // new BaseModelInstance(ModelDefinition, )
      return await UserService.getDownlineLeaders(this.id())
    } catch (ex) {
      console.error(`${ex}`)
      return false
    }
  }

  externalCarrier = () => ({
    all: async () =>
      (await getFactories().UserExternalCarrierFactory.search({
        search: { user_id: this.get('id') },
        pagination: false,
      })) ||
      {} ||
      [],
    create: async data =>
      await getFactories().UserExternalCarrierFactory.create({
        ...(data ? data : {}),
        user_id: this.get('id'),
      }),
  })

  cart = () => ({
    all: async () =>
      (await getFactories().UserContractCartFactory.search({
        search: { user_id: this.get('id') },
        pagination: false,
      })) ||
      {} ||
      [],
    create: async data =>
      await getFactories().UserContractCartFactory.create({
        ...(data ? data : {}),
        user_id: this.get('id'),
      }),
  })

  contract = () => ({
    all: async () =>
      (await getFactories().UsersCarrierContractFactory.search({
        search: { user_id: this.get('id') },
        pagination: false,
      })) ||
      {} ||
      [],
    create: async data =>
      await getFactories().UsersCarrierContractFactory.create({
        ...(data ? data : {}),
        user_id: this.get('id'),
      }),
  })

  contractRequest = () => ({
    all: async () =>
      (await getFactories().UserContractRequestFactory.search({
        search: { user_id: this.get('id') },
        pagination: false,
      })) ||
      {} ||
      [],
    create: async data =>
      await getFactories().UserContractRequestFactory.create({
        ...(data ? data : {}),
        user_id: this.get('id'),
      }),
  })

  meta = useApi => {
    if (useApi === true) {
      return {
        all: async () =>
          (await getFactories().UserMetaFactory.search({
            search: { user_id: this.id() },
            pagination: false,
          })) ||
          {} ||
          [],
        key: async metaKey =>
          (await getFactories().UserMetaFactory.search({
            search: { meta_key: metaKey, user_id: this.id() },
            pagination: false,
          })) ||
          {} ||
          [],
        create: async data =>
          await getFactories().UserMetaFactory.create({
            ...(data ? data : {}),
            user_id: this.id(),
          }),
      }
    }

    return this.children('meta') || []
  }

  corpEmail = useApi => {
    if (useApi === true) {
      return {
        all: async () =>
          (await getFactories().UserCorpEmailFactory.search({
            search: { user_id: this.id() },
            pagination: false,
          })) ||
          {} ||
          [],
      }
    }

    return this.children('corpEmail') || []
  }

  history = useApi => {
    if (useApi === true) {
      return {
        all: async () =>
          (await getFactories().UserEventLogFactory.search({
            search: { user_id: this.id() },
            pagination: false,
          })) ||
          {} ||
          [],
        search: async ({ search, pagination, orderBy }) =>
          await getFactories().UserEventLogFactory.search({
            search: {
              ...(search && typeof search === 'object' ? search : {}),
              user_id: this.id(),
            },
            pagination: pagination ? pagination : pagination === false ? false : pagination,
            order_by: orderBy || {},
          }),
        create: async data =>
          await getFactories().UserEventLogFactory.create({
            ...(data ? data : {}),
            user_id: this.id(),
          }),
      }
    }

    return this.children('history') || []
    // return Array.isArray(this.#children?.history) ? this.#children.history : []
  }

  ledger = useApi => {
    if (useApi === true) {
      return {
        all: async () =>
          (await getFactories().LedgerFactory.search({
            search: { user_id: this.id() },
            pagination: false,
          })) ||
          {} ||
          [],
        create: async data =>
          await getFactories().LedgerFactory.create({
            ...(data ? data : {}),
            user_id: this.id(),
          }),
      }
    }

    return this.children('ledger') || []
    // return Array.isArray(this.#children?.meta) ? this.#children.meta : []
  }

  certification = () => ({
    all: async () =>
      (await getFactories().UserCertificationFactory.search({
        search: { user_id: this.get('id') },
        pagination: false,
      })) ||
      {} ||
      [],
    create: async data =>
      await getFactories().UserCertificationFactory.create({
        ...(data ? data : {}),
        user_id: this.get('id'),
      }),
  })

  getPipLevel = async opts => {
    try {
      return await UserService.getPipLevel()
    } catch (ex) {
      console.error(`Failed to getch PIP level in User model. ${ex}`)
      return
    }
  }

  getTeamPipLevel = async opts => {
    try {
      return await UserService.getTeamPipLevel()
    } catch (ex) {
      console.error(`Failed to getch PIP team level in User model. ${ex}`)
      return
    }
  }

  #stripeData = null
  stripe = opts => (this.#stripeData ? this.#stripeData : (this.#stripeData = new StripeUserService(this, opts)))

  constructor(attribs) {
    super(ModelDefinition, attribs)
  }
}

export default User
